
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarDays, CheckCircle, Lock } from 'lucide-react';
import { Recipe } from '@/types/recipe';
import { MealPlan } from '@/types/mealPlan';
import { MealCompositionWithRecipes } from '@/types/mealComposition';
import { MealNote } from '@/types/mealNote';
import StepIcon from '../StepIcon';
import OnboardingMealPreview from '../OnboardingMealPreview';

interface MealPlanningStepProps {
  personalMealPlansCount: number;
  totalMealPlansCount: number;
  totalAccessibleRecipes: number;
  isActiveFamily: boolean;
  familyMealPlanCount: number;
  nextMeal?: {
    date: string;
    recipes: string[];
    mealType: string;
  } | null;
  mealPlans: MealPlan[];
  recipes: Recipe[];
  mealCompositions: MealCompositionWithRecipes[];
  mealNotes: MealNote[];
  mealDataLoading: boolean;
}

const MealPlanningStep: React.FC<MealPlanningStepProps> = ({ 
  personalMealPlansCount,
  totalMealPlansCount,
  totalAccessibleRecipes,
  isActiveFamily,
  familyMealPlanCount,
  nextMeal,
  mealPlans,
  recipes,
  mealCompositions,
  mealNotes,
  mealDataLoading
}) => {
  const isComplete = personalMealPlansCount > 0;
  const isLocked = totalAccessibleRecipes === 0;
  
  const getMealCountsByType = () => {
    const counts = { breakfast: 0, lunch: 0, dinner: 0 };
    mealPlans.forEach(plan => {
      if (plan.mealType && counts.hasOwnProperty(plan.mealType)) {
        counts[plan.mealType as keyof typeof counts]++;
      }
    });
    return counts;
  };

  const getSpecificMealMessage = () => {
    const counts = getMealCountsByType();
    const totalMeals = personalMealPlansCount;
    
    if (totalMeals === 0) return "";
    
    // Create descriptive text with meal types
    const mealTypes = [];
    if (counts.breakfast > 0) mealTypes.push(`${counts.breakfast} breakfast${counts.breakfast > 1 ? 's' : ''}`);
    if (counts.lunch > 0) mealTypes.push(`${counts.lunch} lunch${counts.lunch > 1 ? 'es' : ''}`);
    if (counts.dinner > 0) mealTypes.push(`${counts.dinner} dinner${counts.dinner > 1 ? 's' : ''}`);
    
    if (totalMeals === 1) {
      if (mealTypes.length > 0 && mealTypes[0]) {
        return `Great! You've planned 1 ${mealTypes[0].split(' ')[1]}.`;
      }
      return `Great! You've planned 1 meal.`;
    }
    
    if (mealTypes.length > 0) {
      return `Great! You've planned ${totalMeals} meals (${mealTypes.join(', ')}).`;
    }
    
    return `Great! You've planned ${totalMeals} meals.`;
  };

  const getStepMessage = () => {
    if (isLocked) {
      return "Complete step 1 first by adding at least one recipe to unlock meal planning.";
    }
    
    if (isComplete) {
      return getSpecificMealMessage();
    }
    
    return "Once you have a recipe, you can create your first meal plan and start organizing your weekly meals.";
  };
  
  const getButtonText = () => {
    if (isActiveFamily) {
      return 'Join Family Meal Planning';
    }
    return 'Plan a Meal';
  };
  
  const getStepTitle = () => {
    if (isActiveFamily) {
      return 'Join Your Family\'s Meal Planning';
    }
    return 'Plan Your First Meals';
  };
  
  return (
    <Card className={`w-full transition-all duration-300 ${
      isLocked 
        ? 'opacity-60 bg-muted/30 border-muted' 
        : isComplete
          ? 'bg-gradient-to-r from-sage/5 to-golden/10 border-sage/20 shadow-md hover:shadow-lg hover:scale-[1.01]'
          : 'bg-white border-border shadow-sm hover:shadow-lg hover:scale-[1.01] hover:border-sage/30'
    }`}>
      <CardHeader className="pb-3 sm:pb-4">
        <div className="flex items-start sm:items-center gap-3 sm:gap-4">
          <StepIcon type="meal-planning" isComplete={isComplete} isLocked={isLocked} />
          <div className="flex-1 min-w-0">
            <CardTitle className={`text-base sm:text-lg font-playfair ${
              isLocked ? 'text-muted-foreground' : ''
            }`}>
              <span className="leading-tight">{getStepTitle()}</span>
            </CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-0 px-4 sm:px-6">
        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
          {getStepMessage()}
        </p>
        {!isLocked ? (
          <Link to="/meal-planner">
            <Button className="w-full transition-all duration-200 hover:shadow-md hover:scale-[1.01] bg-sage hover:bg-sage/90">
              <CalendarDays className="h-4 w-4 mr-2" />
              {getButtonText()}
            </Button>
          </Link>
        ) : (
          <Button disabled className="w-full bg-muted text-muted-foreground cursor-not-allowed">
            <Lock className="h-4 w-4 mr-2" />
            Add a recipe first
          </Button>
        )}
        {isComplete && (
          <div className="mt-4 pt-4 border-t border-sage/20">
            <div className="space-y-3">
              <OnboardingMealPreview
                mealPlans={mealPlans}
                recipes={recipes}
                mealCompositions={mealCompositions}
                mealNotes={mealNotes}
                isLoading={mealDataLoading}
              />
              <div className="flex justify-end">
                <Link to="/meal-planner" className="text-sm font-medium text-sage hover:text-sage/80 transition-colors">
                  View Meal Plan →
                </Link>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MealPlanningStep;
